/**
 * Полоса «Цифровой двойник заполнен N из 6» (AK-900, макет v2 экраны 2–3, 5).
 *
 * ОДИН компонент на два места: шапка «Данных хозяйства» (`/farm/<id>`) и
 * мастер онбординга хозяйства (AK-856, ADR-080 Ф4ф) — там он покажет те же
 * шаги по ходу подключения. Поэтому компонент чистый по пропсам: не знает ни
 * про ручку, ни про роутер, ни про вкладки. Список, порядок и названия шагов
 * приходят готовыми (их задаёт сервер — `GET /farms/{id}/preparation`), клик
 * отдаётся наверх вместе с адресом шага (`href`), а куда и как по нему идти —
 * решает страница.
 *
 * Как читать шаг (статус — формой и подписью, не только цветом):
 *   - `done`                 — галочка, «заполнено · 412 полей»;
 *   - `state=ok`, не `done`  — «!», «осталось разобрать 37 полей» (данные
 *                              есть, но есть сигналы);
 *   - `state=empty`          — пунктирный круг и пунктирная рамка, «не заполнено»;
 *   - `not_available_yet`    — приглушённый шаг «появится после настройки
 *                              людей»: у шага ещё нет источника (ADR-082), это
 *                              не ноль и не ошибка, кликать некуда;
 *   - `no_access`            — замок, «нет доступа к разделу»: ни числа, ни
 *                              цвета — цифра, которую сервер не отдаёт, не
 *                              выдумывается; не кнопка — раздел за правом
 *                              встретил бы человека ошибкой 403.
 *
 * При `doneCount === totalSteps` полоса сворачивается в одну строку
 * «Двойник заполнен · данные от дд.мм.гггг» с кнопкой «Показать шаги»
 * (макет v2, п. «Полоса при 6 из 6 не исчезает»): видно, что данные не устарели,
 * а место на экране не занято. Дата — последняя правка данных ЧЕЛОВЕКОМ
 * (`last_data_change_at`), не время расчёта: расчёт идёт на каждый запрос, и
 * «проверено сегодня» не говорило бы ничего.
 *
 * Адаптив (макет v2 экран 5): 2 колонки на телефоне, 3 на планшете, 6 от 1024.
 *
 * Пропсы — см. `FarmTwinStripProps`. Переиспользование из AK-856: мастер
 * строит `TwinStep[]` из того же ответа ручки (`buildTwinView` в
 * `pages/farm/preparation.ts`) либо из своих промежуточных данных, и передаёт
 * `onStepClick`, если шагам есть куда вести; без него полоса просто показывает.
 */
import { useState } from 'react';
import { CircleAlert, CircleCheck, CircleDashed, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { formatRuDateNumeric } from '@/lib/dateFmt';
import { cn } from '@/lib/utils';

/** Состояние ДАННЫХ шага — так, как его отдаёт сервер. */
export type TwinStepState = 'ok' | 'empty' | 'not_available_yet' | 'no_access';

/** Тон шага — форма значка и цвет. `muted` — «сказать нечем», это не «плохо». */
export type TwinTone = 'ok' | 'warn' | 'alert' | 'muted';

/** Шаг полосы так, как его рисует компонент. */
export interface TwinStep {
  /** Ключ шага (`fields`, `history`, …) — для `data-testid` и React-key. */
  key: string;
  /** Название — с сервера, языком агронома. */
  title: string;
  state: TwinStepState;
  /** Шаг закрыт. Ортогонален `state`: данные могут быть, а сигналы — не разобраны. */
  done: boolean;
  tone: TwinTone;
  /** Подпись статуса словами: «заполнено · 42 единицы», «не заполнено», … */
  label: string;
  /** Пояснение — что именно не так. На телефоне не показывается. */
  detail?: string;
  /** Куда ведёт клик (относительный адрес приложения). `null` — шаг не кликается. */
  href: string | null;
  /** Число так, как оно стоит во вкладке. `null` — числа нет (не ноль). */
  count: number | null;
  /** Знаменатель, если у шага он есть. */
  total: number | null;
}

export interface FarmTwinStripProps {
  /** Шаги в порядке полосы — порядок задаёт сервер, компонент не сортирует. */
  steps: TwinStep[];
  /** Сколько шагов закрыто — с сервера (`done_count`), не пересчитывается здесь. */
  doneCount: number;
  /** Сколько шагов всего (`total_steps`) — знаменатель «N из 6». */
  totalSteps: number;
  /**
   * Последняя правка данных человеком (`last_data_change_at` ручки) — для
   * свёрнутой строки «Двойник заполнен · данные от дд.мм.гггг». `null` —
   * строка без даты. Не время расчёта: оно всегда «сейчас».
   */
  dataAsOf?: string | null;
  /** Подпись справа в шапке («последняя актуализация …»). */
  caption?: string;
  /**
   * Клик по шагу. Вызывается только для шагов, у которых есть `href`, есть
   * источник (не `not_available_yet`) и есть право его читать (не
   * `no_access`); остальные рисуются не кнопкой — см. `isStepClickable`.
   */
  onStepClick?: (step: TwinStep) => void;
  /** Начать свёрнутой при полном заполнении (по умолчанию да). */
  collapsedWhenComplete?: boolean;
  className?: string;
}

const ICON_CLASS: Record<TwinTone, string> = {
  ok: 'text-primary',
  warn: 'text-accent-brand',
  alert: 'text-destructive',
  muted: 'text-muted-foreground',
};

const LABEL_CLASS: Record<TwinTone, string> = {
  ok: 'text-primary',
  warn: 'text-accent-brand',
  alert: 'text-destructive',
  muted: 'text-muted-foreground',
};

const SEGMENT_CLASS: Record<TwinTone, string> = {
  ok: 'bg-primary',
  warn: 'bg-accent-brand',
  alert: 'bg-muted',
  muted: 'bg-muted',
};

/**
 * Колонки от 1024: по числу шагов, чтобы четыре шага старого договора не
 * оставляли две пустые клетки. Полные имена классов — иначе Tailwind их не
 * соберёт.
 */
const LG_COLS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
};

/**
 * Можно ли по шагу кликнуть: есть адрес, есть источник и есть право его
 * читать. `no_access` — не кнопка (решение head-web, ревью #1112): раздел за
 * правом встретит человека ошибкой 403, а тупиковая кнопка хуже её
 * отсутствия; подпись «нет доступа к разделу» уже всё сказала.
 */
export function isStepClickable(step: Pick<TwinStep, 'href' | 'state'>): boolean {
  return !!step.href && step.state !== 'not_available_yet' && step.state !== 'no_access';
}

function StepIcon({ step }: { step: TwinStep }) {
  const cls = cn('h-4 w-4 shrink-0', ICON_CLASS[step.tone]);
  if (step.state === 'no_access') return <Lock className={cls} aria-hidden />;
  if (step.done) return <CircleCheck className={cls} aria-hidden />;
  if (step.state === 'ok') return <CircleAlert className={cls} aria-hidden />;
  // empty / not_available_yet — пунктирный круг: «тут пока пусто».
  return <CircleDashed className={cls} aria-hidden />;
}

function StepBody({ step }: { step: TwinStep }) {
  const dimmed = step.state === 'not_available_yet';
  return (
    <span className="flex min-w-0 flex-col items-start gap-1 text-left">
      <span
        className={cn(
          'flex items-center gap-2 text-sm font-semibold',
          dimmed ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        <StepIcon step={step} />
        {step.title}
      </span>
      <span className={cn('text-xs font-medium', LABEL_CLASS[step.tone])}>{step.label}</span>
      {step.detail && (
        // На телефоне полоса в две колонки — пояснение там не помещается, а
        // значок и подпись уже сказали главное.
        <span className="hidden text-xs font-normal text-muted-foreground md:line-clamp-2">
          {step.detail}
        </span>
      )}
    </span>
  );
}

export function FarmTwinStrip({
  steps,
  doneCount,
  totalSteps,
  dataAsOf = null,
  caption,
  onStepClick,
  collapsedWhenComplete = true,
  className,
}: FarmTwinStripProps) {
  const complete = totalSteps > 0 && doneCount >= totalSteps;
  const [showSteps, setShowSteps] = useState(false);
  const collapsed = complete && collapsedWhenComplete && !showSteps;
  const hasWork = steps.some((s) => !s.done && isStepClickable(s));
  const lgCols = LG_COLS[Math.min(Math.max(steps.length, 1), 6)];

  return (
    <Card
      data-testid="farm-twin-strip"
      data-complete={complete ? 'true' : 'false'}
      className={cn(complete && 'border-primary/50 bg-primary/5', className)}
    >
      <CardContent className={cn('p-4', collapsed ? 'py-3' : 'space-y-3')}>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
              {complete && <CircleCheck className="h-4 w-4 text-primary" aria-hidden />}
              {complete
                ? `Двойник заполнен${dataAsOf ? ` · данные от ${formatRuDateNumeric(dataAsOf)}` : ''}`
                : `Цифровой двойник заполнен ${doneCount} из ${totalSteps}`}
            </span>
            {!complete && hasWork && onStepClick && (
              <span className="text-xs text-muted-foreground">что осталось — нажмите на шаг</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {caption && !collapsed && <span className="text-xs text-muted-foreground">{caption}</span>}
            {complete && collapsedWhenComplete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setShowSteps((v) => !v)}
                aria-expanded={!collapsed}
              >
                {collapsed ? 'Показать шаги' : 'Скрыть шаги'}
              </Button>
            )}
          </div>
        </div>

        {!collapsed && (
          <>
            <div className="flex gap-1" aria-hidden>
              {steps.map((step) => (
                <span
                  key={step.key}
                  className={cn('h-1.5 flex-1 rounded-sm', SEGMENT_CLASS[step.done ? 'ok' : step.tone])}
                />
              ))}
            </div>

            <div className={cn('grid grid-cols-2 gap-2 md:grid-cols-3', lgCols)}>
              {steps.map((step) => {
                const dashed = step.state === 'empty' || step.state === 'not_available_yet';
                return isStepClickable(step) && onStepClick ? (
                  <Button
                    key={step.key}
                    variant="outline"
                    className={cn(
                      'h-auto min-w-0 justify-start whitespace-normal px-3 py-2',
                      dashed && 'border-dashed',
                    )}
                    onClick={() => onStepClick(step)}
                    data-testid={`step-${step.key}`}
                    data-state={step.state}
                  >
                    <StepBody step={step} />
                  </Button>
                ) : (
                  <div
                    key={step.key}
                    className={cn(
                      'rounded-md border border-border px-3 py-2',
                      dashed && 'border-dashed',
                      step.state === 'not_available_yet' && 'bg-muted/40',
                    )}
                    data-testid={`step-${step.key}`}
                    data-state={step.state}
                    aria-disabled={step.state === 'not_available_yet' ? 'true' : undefined}
                  >
                    <StepBody step={step} />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
